import { localTzToUTC } from "@/components/psychologist/calendar/timeZoneHelpers";
import apiCoreAdapter from "@/hooks/apiCoreAdapter";
import { PsychologistFreeScheduleStatus } from "@/components/SessionDebug";
import { addDaysIso } from "@/utils/holidays";
import { findOccupyingSessions, OccupyingSession } from "./formacionBlock";

/** El día completo de la psicóloga: de 00:00 a 24:00 de SU hora, en instantes UTC. */
function dayRangeMs(dateIso: string, timezone: string): { startMs: number; endMs: number } {
  return {
    startMs: localTzToUTC(dateIso, "00:00", timezone).getTime(),
    endMs: localTzToUTC(addDaysIso(dateIso, 1), "00:00", timezone).getTime(),
  };
}

/**
 * Sesiones (no canceladas) de un día, en la zona horaria de la psicóloga — así una psico en
 * Italia ve las sesiones de SU día, no del día en Argentina. Lo usan "Bloquear mi agenda" del
 * aviso de feriado (Comunidad) y el aviso de feriados de la Agenda. Solo consulta: nunca cancela
 * ni modifica una sesión. Lanza si no se pudo consultar.
 */
export async function sessionsOnHoliday(dateIso: string, timezone: string): Promise<OccupyingSession[]> {
  const { startMs, endMs } = dayRangeMs(dateIso, timezone);
  return findOccupyingSessions(startMs, endMs);
}

const HALF_HOUR_MS = 30 * 60_000;
/** Lo que cuenta como "no trabaja ese tramo": bloqueo manual o vacaciones. */
const BLOCKED = new Set<string>([
  PsychologistFreeScheduleStatus.OCCUPIED_MANUAL_BY_PSYCHOLOGIST,
  PsychologistFreeScheduleStatus.OCCUPIED_BY_VACATION,
]);

/**
 * ¿El día completo (en su zona horaria) está bloqueado? Cada tramo de 30 min tiene que estar
 * bloqueado o en vacaciones: un bloqueo de una parte del día no cuenta. Lanza si no se pudo
 * consultar.
 */
export async function isHolidayFullyBlocked(dateIso: string, timezone: string): Promise<boolean> {
  const { startMs, endMs } = dayRangeMs(dateIso, timezone);
  const slots = await apiCoreAdapter.psychologistAvailability({
    from: new Date(startMs).toISOString(),
    to: new Date(endMs).toISOString(),
    source: "psi_platform",
  });
  const blocked = new Set(
    (Array.isArray(slots) ? slots : []).filter((s) => BLOCKED.has(s.status)).map((s) => new Date(s.slot).getTime()),
  );
  for (let t = startMs; t < endMs; t += HALF_HOUR_MS) {
    if (!blocked.has(t)) return false;
  }
  return true;
}
